/**
 * <uterific-exploded> — the three acts of the exploded patch diagram, driven by one
 * scroll progress value.
 *
 *   1. Build    each layer lifts into an axonometric stack, its callout fading in and
 *               drawing an elbow leader out to a dot sitting on that layer.
 *   2. Flood    once the top layer lands, the printed face grows out of its own
 *               silhouette until it floods the frame in lacquer — the mask scales from the
 *               patch shape to full bleed, so the red literally spreads from the patch.
 *   3. Drift    four herb columns rise through the red field at staggered rates. The
 *               heading holds until the fastest column overtakes the others, then fades.
 *
 * Everything is written out as CSS custom properties; the section's stylesheet does the
 * drawing. Geometry that depends on the frame's measured size — the leader elbows, the
 * layer heights they point at — is computed here because CSS cannot measure.
 *
 * The projection is axonometric on purpose: parallel, no perspective, so every layer keeps
 * the same scale and its projected height is predictable, which is what lets the leaders
 * land exactly on their layer.
 *
 * Load this with type="module". It shares helper names with uterific-scroll.js, so as a
 * classic script whichever of the two parsed second would die on a redeclaration.
 */

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
// Herb cards: the fraction of the drift each card takes to arrive, and the point by which
// the last card is home. The step between cards is spread to fit however many there are,
// so a fifth or sixth card lands with the rest instead of past the end of the drift.
const HERB_SPAN = 0.26;
const HERB_HOME = 0.8;

// Projection and pacing. RISE is how many screen pixels one pixel of lift is worth once
// the stack is tilted, so the leaders can find each layer's height.
const TILT = 60;
const RISE = Math.sin((TILT * Math.PI) / 180);
const PAD = 5;

// Where the two cards on each side sit, as fractions of the frame's height: the band runs
// from BAND_TOP down BAND_SPAN, and each card takes the middle of its share. Raising the
// top and lengthening the span lifts the upper card about twice as far as the lower one,
// which is what the stack wants — the printed top and the heat layer are the highest
// sheets, and their cards should be level with them rather than reaching up from mid-frame.
const BAND_TOP = 0.06;
const BAND_SPAN = 0.76;

class UterificExploded extends HTMLElement {
  connectedCallback() {
    this.frameEl = this.querySelector('[data-ut-frame]');
    // The sticky 100svh box, which is a different element from the stage inside it.
    this.pinEl = this.querySelector('.ut-exploded__frame-outer');
    this.layers = Array.from(this.querySelectorAll('[data-ut-layer]'));
    // The compact layout below 1100px shows the same four layers as plain cards, with no
    // room for leaders. They share the layers' timing but are a separate list, so the
    // stack's own indices stay right.
    this.compact = Array.from(this.querySelectorAll('[data-ut-compact]'));
    this.layerTabs = Array.from(this.querySelectorAll('[data-ut-layer-tab]'));
    this.callouts = Array.from(this.querySelectorAll('[data-ut-callout]'));
    this.herbs = Array.from(this.querySelectorAll('[data-ut-herb]'));
    this.counter = this.querySelector('[data-ut-counter]');

    this.liftSetting = parseFloat(this.getAttribute('lift')) || 78;
    this.lift = this.liftSetting;
    this.stackEl = this.querySelector('.ut-exploded__stack');
    this.compactEl = this.querySelector('.ut-exploded__compact');
    this.herbsEl = this.querySelector('.ut-exploded__herbs');
    this.frameOuter = this.querySelector('.ut-exploded__frame-outer');
    this.stacked = window.matchMedia('(max-width: 749px)');
    this.tablet = window.matchMedia('(max-width: 749px)');
    this.startOffset = parseFloat(this.getAttribute('step-offset')) || 0.12;
    this.span = parseFloat(this.getAttribute('step-span')) || 0.2;
    this.floodAt = parseFloat(this.getAttribute('flood-at')) || 0.56;
    this.floodSpan = parseFloat(this.getAttribute('flood-span')) || 0.16;
    this.herbsAt = parseFloat(this.getAttribute('herbs-at')) || 0.72;
    // Drift at which the fastest herb column overtakes the rest. The heading fade and the
    // column motion both read this, so they cannot drift apart.
    this.cross = parseFloat(this.getAttribute('cross')) || 0.55;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.settle();
      return;
    }

    this.box = { w: 1200, h: 600 };
    this.frame = 0;
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.measure());
    };

    this.measure();
    // Above 990px Horizon scrolls inside .page-wrapper, not the window, and scroll events
    // do not bubble — a window listener would never fire and the diagram would sit at
    // progress 0 forever. Capturing on the document catches scroll from the page wrapper
    // and from the window alike, so this works whichever element is doing the scrolling.
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll);
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    this.phoneObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** Reduced motion: show the built stack, skip the flood and the drift entirely. */
  settle() {
    this.style.setProperty('--ut-stack', '1');
    this.style.setProperty('--ut-flood', '0');
    this.style.setProperty('--ut-flood-size', '26');
    this.style.setProperty('--ut-herbs-shown', '0');
    this.layers.forEach((layer, i) => {
      layer.style.setProperty('--ut-t', '1');
      layer.style.setProperty('--ut-z', `${this.lift * i}px`);
    });
    this.compact.forEach((card) => card.style.setProperty('--ut-t', '1'));
    this.callouts.forEach((callout) => callout.style.setProperty('--ut-t', '1'));
    this.herbs.forEach((herb) => {
      herb.style.setProperty('--ut-t', '1');
      herb.style.setProperty('--ut-y', '0px');
    });
  }

  /**
   * Phones: the stack and the botanicals band each reveal once, as they scroll into view.
   * Layers pop into place one after another from the bottom up; the herbs are staggered
   * by the stylesheet from --ut-i. Both stay shown afterwards.
   */
  watchPhone() {
    if (this.phoneObserver || !('IntersectionObserver' in window)) {
      if (!this.phoneObserver) this.revealStack();
      return;
    }
    this.phoneObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target === this.stackEl) this.revealStack();
          if (entry.target === this.herbsEl) this.herbsEl.classList.add('is-in');
          this.phoneObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );
    if (this.stackEl) this.phoneObserver.observe(this.stackEl);
    if (this.herbsEl) this.phoneObserver.observe(this.herbsEl);
  }

  /** Phones: tapping a layer, or its number, shows that layer's words. Bound once. */
  bindTaps() {
    if (this.tapsBound) return;
    this.tapsBound = true;
    this.layers.forEach((layer, i) => {
      layer.addEventListener('click', () => this.select(i));
    });
    this.layerTabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(parseInt(tab.dataset.utLayerTab, 10)));
    });
  }

  select(index) {
    this.active = index;
    this.layers.forEach((layer, i) => layer.classList.toggle('is-active', i === index));
    this.compact.forEach((card) => {
      card.classList.toggle('is-active', parseInt(card.dataset.utLayerIndex, 10) === index);
    });
    this.layerTabs.forEach((tab) => {
      tab.setAttribute('aria-pressed', parseInt(tab.dataset.utLayerTab, 10) === index ? 'true' : 'false');
    });
  }

  revealStack() {
    if (this.stackShown) return;
    this.stackShown = true;
    this.layers.forEach((layer, i) => {
      window.setTimeout(() => layer.style.setProperty('--ut-t', '1'), i * 180);
    });
  }

  measure() {
    this.frame = 0;

    // Phones use the plain stacked layout: nothing here to drive. The red botanicals
    // band is the one thing the header needs to know about, so it carries the mark
    // the section itself carries when the flood is in.
    if (this.stacked.matches) {
      this.removeAttribute('data-ut-header');
      this.herbsEl?.setAttribute('data-ut-header', 'light');
      // The stack stands fully built in the flow, with a shorter lift than the wide
      // layouts so it does not tower over the phone's narrow column.
      const lift = Math.min(this.liftSetting, 52);
      const n = this.layers.length;
      this.layers.forEach((layer, i) => {
        layer.style.setProperty('--ut-z', `${(lift * i).toFixed(1)}px`);
        if (!this.stackShown) layer.style.setProperty('--ut-t', '0');
      });
      this.style.setProperty('--ut-stack-rise', `${(RISE * lift * Math.max(0, n - 1)).toFixed(0)}px`);
      this.herbs.forEach((herb, i) => herb.style.setProperty('--ut-i', String(i)));
      this.watchPhone();
      this.bindTaps();
      // Layer 1, the printed top layer, is the last in the stack's DOM order.
      if (this.active === undefined) this.select(n - 1);
      return;
    }
    this.herbsEl?.removeAttribute('data-ut-header');
    if (this.phoneObserver) {
      this.phoneObserver.disconnect();
      this.phoneObserver = null;
    }

    const rect = this.getBoundingClientRect();
    const travel = rect.height - window.innerHeight * 0.4;
    const p = travel <= 0 ? 1 : clamp((window.innerHeight * 0.6 - rect.top) / travel);

    if (this.frameEl) {
      const f = this.frameEl.getBoundingClientRect();
      if (Math.abs(this.box.w - f.width) > 1 || Math.abs(this.box.h - f.height) > 1) {
        this.box = { w: f.width, h: f.height };
      }
    }

    // Tablets have no leaders to pace the build, and their cards arrive only once the
    // stack is complete — so the stack builds faster, the cards get a clear window to be
    // read, and the flood waits for that window to close. On desktop the flood follows the
    // last layer directly, as set on the section.
    const tm = this.tablet.matches
      ? { start: 0.08, span: 0.16, floodAt: 0.68, herbsAt: 0.84 }
      : { start: this.startOffset, span: this.span, floodAt: this.floodAt, herbsAt: this.herbsAt };
    this.timing = tm;

    const flood = easeOut(clamp((p - tm.floodAt) / this.floodSpan));
    const drift = clamp((p - tm.herbsAt) / (1 - tm.herbsAt));
    const stack = 1 - flood;

    this.style.setProperty('--ut-p', p.toFixed(4));
    this.style.setProperty('--ut-stack', stack.toFixed(4));
    this.style.setProperty('--ut-flood', flood.toFixed(4));
    // The ground is sand until the flood, so the header's white logo is only asked for
    // once the lacquer has actually reached the corners of the frame, not for the whole
    // section. The mask grows to (26 + flood * 760)% of the frame's width from its centre,
    // so at 0.3 it is 254% — wide, but a patch silhouette that wide still leaves the top
    // corners sand, which is where the wordmark sits. At 0.55 it is 444% and the corners
    // are red. Raised, because the header now takes this mark at its word: it cannot
    // measure the flood itself, which is pointer-events: none and invisible to a hit test.
    //
    // Carried by the pinned frame, not by this element. This element is 560svh tall, so
    // once the flood is up it overlaps the header for the rest of the section — including
    // the long tail after the act is behind you, where the ground under the bar is the
    // sand band again. The header read that as "still red" and went white on a light
    // panel. The frame is 100svh and sticks to the top, so it covers the header exactly
    // while the act is on screen and stops the moment the section scrolls away.
    // The pinned box, not the stage inside it: [data-ut-frame] sits below the heading with
    // padding above it, so its top edge never reaches the header and the mark it carried
    // was never seen. The pin is sticky at top: 0 and 100svh, so it covers the bar for
    // exactly as long as the act is on screen and no longer.
    const marker = this.pinEl || this;
    if (flood > 0.55) marker.setAttribute('data-ut-header', 'light');
    else marker.removeAttribute('data-ut-header');
    if (marker !== this) this.removeAttribute('data-ut-header');
    // The mask grows from the patch's own footprint to well past the frame.
    this.style.setProperty('--ut-flood-size', (26 + flood * 760).toFixed(1));
    this.style.setProperty('--ut-herbs-shown', clamp((flood - 0.5) / 0.5).toFixed(4));
    this.style.setProperty('--ut-heading-fade', '1');

    const n = this.layers.length;

    // Tablets: the cards sit at the foot of the frame, so the stack only has the room
    // above them. Centre it there and cap the lift so the built stack fits that room —
    // a fixed lift climbed straight through the heading on a portrait tablet.
    this.lift = this.liftSetting;
    if (this.tablet.matches && this.stackEl && this.compactEl && this.box.h) {
      const cards = this.compactEl.getBoundingClientRect().height;
      const room = Math.max(120, this.box.h - cards - 16);
      const footprint = this.stackEl.getBoundingClientRect().height;
      const maxLift = (room * 0.9 - footprint) / (RISE * Math.max(1, n - 1));
      this.lift = Math.max(14, Math.min(this.liftSetting, maxLift));
      this.style.setProperty('--ut-top', `${(room / 2).toFixed(1)}px`);
    } else {
      this.style.removeProperty('--ut-top');
    }

    // Half the stack's projected height, so the built stack stays centred on the frame
    // instead of climbing out of it as layers are added.
    const shift = (RISE * this.lift * (n - 1)) / 2;
    this.style.setProperty('--ut-shift', `${shift.toFixed(1)}px`);

    this.layers.forEach((layer, i) => {
      const t = easeOut(clamp((p - i * tm.start) / tm.span));
      layer.style.setProperty('--ut-t', t.toFixed(4));
      layer.style.setProperty('--ut-z', `${(this.lift * i * t).toFixed(1)}px`);
    });

    // The cards wait for the whole stack to build, then arrive together with a light
    // stagger in reading order, so the drawing is read first and the words second.
    const cardsAt = (n - 1) * tm.start + tm.span;
    this.compact.forEach((card, i) => {
      const t = easeOut(clamp((p - cardsAt - i * 0.03) / (tm.span * 0.6))) * stack;
      card.style.setProperty('--ut-t', t.toFixed(4));
    });

    if (this.counter) {
      this.counter.textContent = `${Math.min(n, Math.floor(p / tm.start) + 1)} / ${n}`;
    }

    this.placeCallouts(p, stack);

    // The botanicals fly up into place one after another, left to right: each card
    // starts once the one before it is most of the way home, rising from 160px below
    // its slot while it fades in. The heading stays put above them.
    const step = this.herbs.length > 1 ? (HERB_HOME - HERB_SPAN) / (this.herbs.length - 1) : 0;
    this.herbs.forEach((herb, i) => {
      const t = easeOut(clamp((drift - i * step) / HERB_SPAN));
      herb.style.setProperty('--ut-y', `${((1 - t) * 160).toFixed(1)}px`);
      herb.style.setProperty('--ut-t', t.toFixed(4));
    });

    // Narrow screens: the band is taller than the pinned frame, so the drift also carries
    // it up through the frame, bottom cards included.
    if (this.tablet.matches && this.herbsEl && this.frameOuter) {
      const overflow = this.herbsEl.offsetHeight - this.frameOuter.clientHeight;
      const scroll = overflow > 0 ? -overflow * drift : 0;
      this.style.setProperty('--ut-herbs-scroll', `${scroll.toFixed(1)}px`);
    } else {
      this.style.removeProperty('--ut-herbs-scroll');
    }
  }

  /**
   * Cards fill their column in evenly spaced slots and the leader does the reaching, so
   * nothing floats and nothing collides. Slots are assigned so that higher layers take
   * higher slots — that makes both leaders in a column monotonic, and therefore impossible
   * to cross.
   *
   * Where each leader ENDS is measured off the layer it points at, never derived from the
   * projection. The stack is sized in viewport-relative units and the cards are capped in
   * pixels, so the gap between the two changes with the viewport: a leader of any computed
   * length lands somewhere different on every screen. Measuring the layer is the only way
   * the dot sits on it at every width.
   *
   * Reads are batched ahead of writes. The layer transforms were set earlier this frame,
   * so the first getBoundingClientRect forces one layout; writing between reads would
   * force one per callout.
   */
  placeCallouts(p, stack) {
    if (!this.callouts.length || !this.frameEl) return;

    const frame = this.frameEl.getBoundingClientRect();
    if (!frame.width || !frame.height) return;

    const sideOf = (i) => (i % 2 === 0 ? 'left' : 'right');
    const countOn = (side) => this.callouts.filter((_, i) => sideOf(i) === side).length;

    // --- Read pass -------------------------------------------------------------
    const plan = this.callouts.map((callout, i) => {
      const side = sideOf(i);
      const before = this.callouts.slice(0, i).filter((_, x) => sideOf(x) === side).length;
      const m = countOn(side);
      const slot = m - 1 - before;

      // The band the cards are spread through, as a fraction of the frame's height. It
      // starts high and runs long so the upper card on each side sits beside the layers it
      // names — the printed top and the heat layer are at the top of the lifted stack, and
      // a card centred at a third of the frame pointed up at them from below.
      const y = frame.height * (BAND_TOP + (slot + 0.5) * (BAND_SPAN / m));
      const tm = this.timing || { start: this.startOffset, span: this.span };
      const t = easeOut(clamp((p - i * tm.start) / tm.span)) * stack;

      const layer = this.layers[i];
      const card = callout.querySelector('[data-ut-card]');
      // The pin marks a point that sits on the silhouette; reading its rect lets the
      // browser apply the layer's rotation, so this is where the sheet really is on
      // screen rather than where the projection says it ought to be.
      const pin = layer && layer.querySelector(
        side === 'left' ? '[data-ut-pin-left]' : '[data-ut-pin-right]'
      );
      if (!pin || !card) return { callout, side, y, t, run: 0, dy: 0 };

      const point = pin.getBoundingClientRect();
      const edge = card.getBoundingClientRect();
      const cardX = (side === 'left' ? edge.right : edge.left) - frame.left;

      return {
        callout,
        side,
        y,
        t,
        run: Math.max(PAD * 2, Math.abs(point.left - frame.left - cardX)),
        dy: point.top - frame.top - y,
      };
    });

    // --- Write pass ------------------------------------------------------------
    for (const { callout, side, y, t, run, dy } of plan) {
      callout.style.setProperty('--ut-t', t.toFixed(4));
      callout.style.setProperty('--ut-top', `${y.toFixed(1)}px`);
      callout.style.setProperty('--ut-dx', side === 'left' ? '-1' : '1');
      this.drawLeader(callout, run, dy, side, t);
    }
  }

  /**
   * A horizontal run out of the card, then a diagonal to the dot sitting on the layer.
   * `run` is the measured distance to that layer, so the dot ends exactly on it.
   */
  drawLeader(callout, run, dy, side, t) {
    const svg = callout.querySelector('[data-ut-leader]');
    if (!svg) return;

    const h = Math.abs(dy) + PAD * 2;
    const cardY = dy < 0 ? h - PAD : PAD; // where the run leaves the card
    const dotY = dy < 0 ? PAD : h - PAD; // where the dot sits, on the layer
    const bend = run * 0.42;
    const len = bend + Math.hypot(run - bend, Math.abs(dotY - cardY));

    svg.setAttribute('width', run);
    svg.setAttribute('height', h);
    svg.setAttribute('viewBox', `0 0 ${run} ${h}`);
    svg.style.top = `${dy < 0 ? dy - PAD : -PAD}px`;
    svg.style[side === 'left' ? 'left' : 'right'] = '100%';
    svg.style.transform = side === 'left' ? 'none' : 'scaleX(-1)';

    const path = svg.querySelector('path');
    if (path) {
      path.setAttribute('d', `M 0 ${cardY} L ${bend} ${cardY} L ${run} ${dotY}`);
      // The line draws itself in as the layer arrives.
      path.setAttribute('stroke-dasharray', len);
      path.setAttribute('stroke-dashoffset', len * (1 - t));
    }

    const dot = svg.querySelector('circle');
    if (dot) {
      dot.setAttribute('cx', run);
      dot.setAttribute('cy', dotY);
      dot.setAttribute('opacity', t);
    }
  }
}

if (!customElements.get('uterific-exploded')) {
  customElements.define('uterific-exploded', UterificExploded);
}
